import React from 'react';
import { Layers3 } from 'lucide-react';
import { FloatingLegendCard } from '../../../components/legend/FloatingLegendCard';
import { cn } from '../../../lib/utils';
import { useDiagramStore } from '../../../stores/diagramStore';
import {
  type ProcessOverlayKey,
  useProcessViewStore,
} from '../../../stores/processViewStore';
import { useProcessCanvasLayerStore } from '../journey/processCanvasLayerStore';

const OVERLAY_ARRAY_KEYS = [
  'controls',
  'controlPoints',
  'evidence',
  'findings',
  'thirdPartyDependencies',
  'botDependencies',
  'modelDependencies',
] as const;

function hasValues(value: unknown): boolean {
  return Array.isArray(value) && value.length > 0;
}

function nodeHasProcessOverlayData(node: Record<string, unknown>): boolean {
  const props = node.props && typeof node.props === 'object'
    ? node.props as Record<string, unknown>
    : {};
  const attrs = node.attrs && typeof node.attrs === 'object'
    ? node.attrs as Record<string, unknown>
    : props.attrs && typeof props.attrs === 'object'
      ? props.attrs as Record<string, unknown>
      : {};

  return OVERLAY_ARRAY_KEYS.some((key) => hasValues(attrs[key]))
    || hasValues(node.tags)
    || hasValues(props.tags)
    || hasValues(attrs.tags);
}

interface ProcessOverlayLegendItem {
  key: ProcessOverlayKey;
  label: string;
  description: string;
  marker: React.ReactNode;
}

const LEGEND_ITEMS: ProcessOverlayLegendItem[] = [
  {
    key: 'complianceBorder',
    label: 'Compliance border',
    description: 'Colored outline shows overall assurance coverage.',
    marker: <span className="h-3.5 w-3.5 rounded-sm border-2 border-amber-500" />,
  },
  {
    key: 'controlStatus',
    label: 'Control status',
    description: 'Corner dot shows mapped controls or control points.',
    marker: <span className="h-3 w-3 rounded-full bg-emerald-500 ring-1 ring-white" />,
  },
  {
    key: 'evidenceStatus',
    label: 'Evidence',
    description: 'Check mark shows attached evidence status.',
    marker: (
      <span className="flex h-3.5 w-3.5 items-center justify-center rounded-full bg-emerald-500 text-[9px] font-bold leading-none text-white">
        ✓
      </span>
    ),
  },
  {
    key: 'dependencies',
    label: 'Dependencies',
    description: 'Pills mark third-party, bot, and model dependencies.',
    marker: (
      <span className="rounded-[3px] bg-indigo-500 px-1 text-[8px] font-bold leading-3 text-white">
        3P
      </span>
    ),
  },
  {
    key: 'tags',
    label: 'Tags',
    description: 'Chips show process tags imported from SOP metadata.',
    marker: <span className="h-3.5 w-5 rounded-full border border-slate-300 bg-slate-100" />,
  },
];

interface ProcessOverlayLegendProps {
  stacked?: boolean;
}

export const ProcessOverlayLegend: React.FC<ProcessOverlayLegendProps> = ({ stacked = false }) => {
  const overlayVisibility = useProcessViewStore((s) => s.overlayVisibility);
  const toggleOverlay = useProcessViewStore((s) => s.toggleOverlay);
  const complianceActive = useProcessCanvasLayerStore((s) => s.complianceActive);
  const hasOverlayData = useDiagramStore((s) => s.nodes.some((node) => (
    nodeHasProcessOverlayData(node as unknown as Record<string, unknown>)
  )));

  // The controls are relevant only when the folder has opted into the
  // Compliance layer and this diagram contains something they can affect.
  if (!complianceActive || !hasOverlayData) return null;

  return (
    <FloatingLegendCard
      stacked={stacked}
      defaultCollapsed
      title="Process overlays"
      icon={<Layers3 className="h-3.5 w-3.5 text-slate-500" />}
      testId="tid.process.overlays.legend"
      toggleTestId="tid.process.overlays.legend.toggle"
      className="pointer-events-auto min-w-[210px]"
      bodyClassName="px-2 pb-2 pt-1.5"
      contentClassName="gap-1"
    >
      {LEGEND_ITEMS.map((item) => {
        const active = overlayVisibility[item.key];
        return (
          <button
            key={item.key}
            type="button"
            className={cn(
              'flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left transition-colors',
              'hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
              'dark:hover:bg-slate-800',
              !active && 'opacity-45',
            )}
            title={item.description}
            aria-pressed={active}
            data-testid={`tid.process.overlays.legend.${item.key}`}
            onClick={() => toggleOverlay(item.key)}
          >
            <span className="flex h-5 w-6 shrink-0 items-center justify-center" aria-hidden="true">
              {item.marker}
            </span>
            <span
              className={cn(
                'min-w-0 flex-1 truncate text-xs text-slate-700 dark:text-slate-200',
                !active && 'line-through decoration-slate-400',
              )}
            >
              {item.label}
            </span>
          </button>
        );
      })}
    </FloatingLegendCard>
  );
};
